import Seo from "@/components/Seo";
import { useCurrency } from "@/context/CurrencyContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  Lock,
  Handshake,
  FileSignature,
  Wallet,
  Hammer,
  Search,
  BadgeCheck,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

const NAVY = "#0f172a";
const ORANGE = "#fb3700";

const ppmEnquiryLink = `/contact?service=${encodeURIComponent(
  "Project Payment Management"
)}&message=${encodeURIComponent(
  "I'd like to register a project under Project Payment Management. Please share the next steps."
)}`;

const steps = [
  {
    icon: FileSignature,
    title: "Sign the agreement",
    text: "All three parties sign a Project Payment Management Agreement.",
  },
  {
    icon: Wallet,
    title: "Client deposits funds",
    text: "The client deposits the full project funds with ProToCall.",
  },
  {
    icon: Hammer,
    title: "Work begins, secured",
    text: "The contractor begins work knowing payment is already secured.",
  },
  {
    icon: Search,
    title: "We inspect milestones",
    text: "ProToCall inspects the work at each agreed milestone.",
  },
  {
    icon: BadgeCheck,
    title: "Funds released",
    text: "Payment is released to the contractor upon verified completion.",
  },
];

const feeTable = [
  { size: "Small", fee: "8%", value: (f) => `${f(10000, { decimals: 0 })} and below` },
  { size: "Medium", fee: "6%", value: (f) => `${f(10001, { decimals: 0 })} – ${f(25000, { decimals: 0 })}` },
  { size: "Large", fee: "4%", value: (f) => `Above ${f(25000, { decimals: 0 })}` },
];

export default function ProjectPaymentPage() {
  const { formatMoney } = useCurrency();
  return (
    <div className="bg-white" data-testid="ppm-page">
      <Seo
        title="Project Payment Management | ProToCall"
        description="ProToCall's Project Payment Management holds your project funds in trust and releases payment to the contractor only after the work is inspected and verified as complete. Bank-grade protection for property owners and contractors in Saint Lucia."
        path="/project-payment-management"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Project Payment Management",
          serviceType: "Construction escrow / payment protection",
          provider: { "@type": "HomeAndConstructionBusiness", name: "ProToCall Ltd." },
          areaServed: { "@type": "AdministrativeArea", name: "Saint Lucia" },
          description:
            "A neutral financial intermediary service that holds project funds in trust and releases payment to contractors only after work is verified complete.",
        }}
      />

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ backgroundColor: NAVY }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider mb-6"
              style={{ backgroundColor: "rgba(212,82,26,0.18)", color: "#ffc2b0" }}
            >
              <ShieldCheck className="w-4 h-4" />
              Bank-grade payment protection
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              Project Payment Management
            </h1>
            <p className="text-lg md:text-xl text-slate-200 leading-relaxed mb-9">
              Your money is protected until the work is done. The contractor's payment is
              guaranteed when it is.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                className="h-12 px-8 rounded-full text-white font-semibold shadow-lg hover:opacity-90"
                style={{ backgroundColor: ORANGE }}
                data-testid="ppm-hero-cta"
              >
                <Link to={ppmEnquiryLink}>
                  Register a Project
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                className="h-12 px-8 rounded-full font-semibold border-2 border-white/40 text-white bg-transparent hover:bg-white hover:text-slate-900"
                data-testid="ppm-hero-secondary"
              >
                <a href="#how-it-works">See how it works</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Body copy */}
      <section className="py-16 md:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div
            className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6"
            style={{ backgroundColor: "rgba(15,23,42,0.06)" }}
          >
            <Handshake className="w-7 h-7" style={{ color: NAVY }} />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: NAVY }}>
            A neutral party you can both trust
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            ProToCall acts as a neutral financial intermediary between property owners and
            contractors. We hold your project funds in trust and release payment to the contractor
            only after the work has been inspected and verified as complete. No more paying upfront
            and hoping for the best. No more contractors worrying about clients who won't pay.
          </p>
        </div>
      </section>

      {/* Two-column callout */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-6 lg:gap-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="rounded-3xl p-8 md:p-10 text-white"
            style={{ backgroundColor: NAVY }}
            data-testid="ppm-clients-card"
          >
            <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-6">
              <Lock className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-3">For Clients</h3>
            <p className="text-slate-200 leading-relaxed">
              Your funds are secured with ProToCall before work begins. The contractor cannot touch
              a cent until ProToCall confirms the work meets the agreed standard.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-3xl p-8 md:p-10 text-white"
            style={{ backgroundColor: ORANGE }}
            data-testid="ppm-contractors-card"
          >
            <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center mb-6">
              <BadgeCheck className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-3">For Contractors</h3>
            <p className="text-white/90 leading-relaxed">
              Payment is guaranteed from day one. Once ProToCall holds the funds, you will be paid
              when the work is done — no chasing, no disputes.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-16 md:py-24 bg-slate-50 scroll-mt-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span
              className="text-sm font-semibold uppercase tracking-wider"
              style={{ color: ORANGE }}
            >
              Simple & transparent
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mt-3" style={{ color: NAVY }}>
              How It Works
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="relative bg-white rounded-2xl p-6 border border-slate-200 shadow-sm"
                data-testid={`ppm-step-${i + 1}`}
              >
                <div
                  className="absolute -top-3 -left-3 w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-bold shadow"
                  style={{ backgroundColor: ORANGE }}
                >
                  {i + 1}
                </div>
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: "rgba(15,23,42,0.06)" }}
                >
                  <step.icon className="w-5 h-5" style={{ color: NAVY }} />
                </div>
                <h3 className="font-semibold mb-2" style={{ color: NAVY }}>
                  {step.title}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fee table */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: NAVY }}>
              Clear, fair pricing
            </h2>
            <p className="text-slate-600 mt-3">
              The fee is deducted from the contractor's final payment.{" "}
              <span className="font-semibold" style={{ color: NAVY }}>
                No extra charge to the client.
              </span>
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl border border-slate-200 shadow-sm">
            <table className="w-full text-left" data-testid="ppm-fee-table">
              <thead>
                <tr className="text-white" style={{ backgroundColor: NAVY }}>
                  <th className="px-5 md:px-8 py-4 text-sm font-semibold">Project Size</th>
                  <th className="px-5 md:px-8 py-4 text-sm font-semibold">Contract Value</th>
                  <th className="px-5 md:px-8 py-4 text-sm font-semibold text-right">Fee</th>
                </tr>
              </thead>
              <tbody>
                {feeTable.map((row, i) => (
                  <tr
                    key={row.size}
                    className={i % 2 === 1 ? "bg-slate-50" : "bg-white"}
                    data-testid={`ppm-fee-row-${row.size.toLowerCase()}`}
                  >
                    <td className="px-5 md:px-8 py-5 font-semibold" style={{ color: NAVY }}>
                      {row.size}
                    </td>
                    <td className="px-5 md:px-8 py-5 text-slate-600">{row.value(formatMoney)}</td>
                    <td
                      className="px-5 md:px-8 py-5 text-right text-lg font-bold"
                      style={{ color: ORANGE }}
                    >
                      {row.fee}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pb-20 md:pb-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div
            className="rounded-3xl px-8 py-14 md:p-16 text-center text-white relative overflow-hidden"
            style={{ backgroundColor: NAVY }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to protect your next project?
            </h2>
            <p className="text-slate-200 max-w-2xl mx-auto mb-8">
              Register your project with ProToCall and give both sides the confidence to get started
              — funds secured, standards enforced, everyone protected.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                className="h-12 px-8 rounded-full text-white font-semibold shadow-lg hover:opacity-90"
                style={{ backgroundColor: ORANGE }}
                data-testid="ppm-final-cta"
              >
                <Link to={ppmEnquiryLink}>
                  Register a Project
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </Button>
              <div className="flex items-center justify-center gap-2 text-slate-200 text-sm">
                <CheckCircle2 className="w-4 h-4" style={{ color: "#ffc2b0" }} />
                No upfront cost to enquire
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
